import React, { useEffect } from 'react';
import { Flex, Typography } from 'antd';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useIntl } from 'react-intl';
import { CustomCardContainer, BackButton } from 'shared/ui';
import { CodeInput } from 'shared/ui';
import { useLoginMutation } from '../../login/api';
import { useLazyGetUserInfoQuery, useSaveGtagMutation } from 'entities/user';
import { setActivity } from 'shared/config/store/reducers/appSlice';
import { setUserMeta } from 'shared/config/store/reducers/userSlice';
import { useMessage } from 'shared/libs/hooks/useMessage/useMessage';
import { handleErrorWithCommonFallback } from 'shared/libs/utils/helpers';
import { ROUTES } from 'shared/const/routes';
import { AppDispatch } from 'shared/config/store/store';
import classes from './TwoFactorPage.module.scss';
import { GTAG_ID } from 'shared/const/app';

const { Paragraph } = Typography;

const TwoFactorPage = () => {
  const { state } = useLocation();
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { formatMessage: t } = useIntl();
  const { showError, showSuccess } = useMessage();

  const [login] = useLoginMutation();
  const [fetchUserInfo] = useLazyGetUserInfoQuery();
  const [saveGtag] = useSaveGtagMutation();

  useEffect(() => {
    if (!state?.email || !state?.password) {
      navigate(ROUTES.LOGIN.path);
    }
  }, [state, navigate]);

  const onCodeEntered = async (code: string) => {
    if (!state?.email || !state?.password) {
      navigate(ROUTES.LOGIN.path);
      return;
    }

    dispatch(setActivity(true));
    try {
      const { response } = await login({
        email: state.email,
        password: state.password,
        google2faCodeFromUser: code,
      }).unwrap();
      
      gtag('get', GTAG_ID, 'client_id', (clientID) => {
        saveGtag({ clientId: clientID as string, eventName: 'web_login_successful', gtag: GTAG_ID });
        gtag('event', 'web_login_successful', {
          user_id: response.id,
          email: response.email,
          verified: response.verified,
        });
      });

      if (!response.verified && response.email) {
        navigate({
          pathname: ROUTES.CONFIRM_EMAIL.path,
          search: `email=${response.email}&phone=${response.phone}`,
        });
      } else if (!response.phoneVerified && response.phone) {
        navigate(ROUTES.CONFIRM_PHONE.path);
      } else {
        showSuccess(t({ id: 'loginPage.welcome' }));
        dispatch(setUserMeta(response));
        fetchUserInfo();
        navigate(ROUTES.DASHBOARD.path);
      }
    } catch (error: any) {
      handleErrorWithCommonFallback(error.response, t, showError);
    } finally {
      dispatch(setActivity(false));
    }
  };

  if (!state?.email || !state?.password) {
    return null;
  }

  return (
    <div className={classes.wrapper}>
      <BackButton onClick={() => navigate(ROUTES.LOGIN.path)} />
      <Flex flex={1} justify="center" align="center">
        <CustomCardContainer className={classes.card}>
          <Typography.Text className={classes.companyName}>Weststein</Typography.Text>
          <Typography.Text className={classes.title}>
            {t({ id: 'screens.auth.two.factor.title' })}
          </Typography.Text>
          <Paragraph className={classes.desc}>
            {t({ id: 'screens.auth.two.factor.legend' })}
          </Paragraph>
          <CodeInput onEnter={onCodeEntered} />
        </CustomCardContainer>
      </Flex>
    </div>
  );
};

export default TwoFactorPage; 